import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import { MapPin, Menu, Mic, MoveUpRight, Navigation } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import MapSelector from './MapSelector';

// Styled components for the signup form
const SignupContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
    min-height: 100vh;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SignupModal = styled.div`
  width: 647px;
  background: #000000;
  border-radius: 25px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
    @media (max-width: 768px) {
    width: 100%;
    max-width: 500px;
    height: auto;
    min-height: 700px;
    padding: 32px 24px;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 24px 20px;
    border-radius: 16px;
    min-height: 650px;
  }
  
  @media (max-width: 360px) {
    padding: 20px 16px;
    border-radius: 12px;
    min-height: 600px;
  }
`;

const SignupHeader = styled.div`
  margin-bottom: 28px;
  
  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 24px;
  }
  
  h2 {
    color: #ffffff;
    font-size: 25px;
    font-weight: 600;
    margin: 0;
    text-align: center;
        @media (max-width: 768px) {
      font-size: 22px;
    }
    
    @media (max-width: 480px) {
      font-size: 20px;
    }
    
    @media (max-width: 360px) {
      font-size: 18px;
    }
  }
`;

const SignupForm = styled.form`
  width: 100%;
  max-width: 460px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 28px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
  label {
    display: block;
    color: #ffffff;
    font-size: 16px;
    margin-bottom: 8px;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 15px;
      margin-bottom: 6px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
      margin-bottom: 5px;
    }
  }
`;

const FormInput = styled.input`
  width: 100%;
  height: 46px;
  background: rgba(217, 217, 217, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0 15px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    height: 50px;
    padding: 0 16px;
    font-size: 15px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    height: 45px;
    padding: 0 14px;
    font-size: 14px;
    border-radius: 8px;
  }
  
  @media (max-width: 360px) {
    height: 42px;
    padding: 0 12px;
    font-size: 13px;
    border-radius: 6px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 17px;
  margin-bottom: 28px;
`;

const FormGroupRow = styled(FormGroup)`
  flex: 1;
  margin-bottom: 0;
`;

const AddressSection = styled.div`
  margin-bottom: 28px;
`;

const AddressTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const MapPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

const MapIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(244, 63, 96, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F43F60;
`;

const MapText = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const MapButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 8px;
  padding: 8px 16px;
  color: #F43F60;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

const CoordinatesRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const CoordinateInput = styled(FormInput)`
 height: 46px;
  font-size: 16px;
`;

const LocationButton = styled.button`
  width: 100%;
  height: 45px;
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 12px;
  color: #F43F60;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    border-color: rgba(244, 63, 96, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SignupButton = styled.button`
  width: 100%;
  height: 50px;
  background: #F43F60;
  border: none;
  border-radius: 12px;
  color: #EDEDED;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #E03A56;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const Divider = styled.div`
  width: 100%;
  max-width: 460px;
  text-align: center;
  margin: 30px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  span {
    background: #000000;
    color: #9E9E9E;
    padding: 0 20px;
    font-size: 16px;
    position: relative;
    z-index: 1;
  }
`;

const SocialLogin = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 30px;
`;

const SocialButton = styled.button`
  width: 100px;
  height: 53px;
  background: #363636;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #404040;
  }
  
  @media (max-width: 768px) {
    width: auto;
    height: 50px;
    padding: 0 16px;
  }
  svg {
    width: 24px;
    height: 24px;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: auto;
  
  span {
    color: #D4D4D4;
    font-size: 16px;
    margin-right: 10px;
  }
  
  .login-link {
    color: #F43F60;
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid #F43F60;
  color: #F43F60;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  color: #22C55E;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;

const Signup = () => {
  const navigate = useNavigate();

  // Signup form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    dealerInfo: {
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        latitude: null,
        longitude: null,
      },
      phone: ''
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (error) setError('');
  };

  // Get current location
  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData(prev => ({
            ...prev,
            dealerInfo: {
              ...prev.dealerInfo,
              address: {
                ...prev.dealerInfo.address,
                latitude: latitude,
                longitude: longitude
              }
            }
          }));
          setIsGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setError('Unable to get your location. Please use the map to select your location.');
          setIsGettingLocation(false);
        }
      );
    } else {
      setError('Geolocation is not supported by this browser. Please use the map to select your location.');
      setIsGettingLocation(false);
    }
  };

  // Handle map location selection
  const handleMapLocationSelect = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      dealerInfo: {
        ...prev.dealerInfo,
        address: {
          ...prev.dealerInfo.address,
          latitude: lat,
          longitude: lng
        }
      }
    }));
  };

  // Handle map click
  const handleMapClick = () => {
    setIsMapOpen(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    // Validate required address fields
    const { address } = formData.dealerInfo;
    if (!address.street || !address.city || !address.state || !address.zip || !address.country) {
      setError('Please fill in all address fields');
      setIsLoading(false);
      return;
    }

    // Validate coordinates
    if (address.latitude === null || address.longitude === null) {
      setError('Please set your location coordinates using the map or GPS');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/dealer/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          dealerInfo: formData.dealerInfo
        })
      });

      const data = await response.json();
      console.log(data);

      if (response.ok) {
        setSuccess('Signup successful! Please check your email to verify your account.');
        // Store token in sessionStorage
        sessionStorage.setItem('dealerToken', data.data.token);
        sessionStorage.setItem('dealer', JSON.stringify(data.data.user));

        // Redirect to dashboard or home page
        setTimeout(() => {
          window.location.href = '/dashboard'; // or wherever you want to redirect
        }, 1500);
      } else {
        setError(data.message || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Signup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // --- UI ---
  return (
    <>
      {/* Main Content */}
      <main className="main-content">
        <section className="search-area home" style={{ backgroundImage: 'url(/home-bg.png)', backgroundSize: 'cover', backgroundPosition: 'top', backgroundRepeat: 'no-repeat' }}>
          <div className="containers">
            <div className="row justify-content-center" >
              <div className="col-md-12 col-lg-10 col-xl-10" >

                <SignupContainer>
                  <SignupModal>
                    <SignupHeader>
                      <h2>Dealer Sign up</h2>
                    </SignupHeader>

                    <SignupForm onSubmit={handleSubmit}>
                      {error && (
                        <ErrorMessage>
                          {error}
                        </ErrorMessage>
                      )}

                      {success && (
                        <SuccessMessage>
                          {success}
                        </SuccessMessage>
                      )}

                      <FormGroup>
                        <label htmlFor="name">Dealer Name</label>
                        <FormInput
                          type="text"
                          id="name"
                          name="dealerInfo.name"
                          value={formData.dealerInfo.name}
                          onChange={handleInputChange}
                          placeholder="Enter dealer name"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="email">Email</label>
                        <FormInput
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                          <label htmlFor="phone">Phone</label>
                          <FormInput
                            type="tel"
                            id="phone"
                            name="dealerInfo.phone"
                            value={formData.dealerInfo.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                            required
                          />
                      </FormGroup>

                      <AddressSection>
                        <AddressTitle>
                          <MapPin size={20} />
                          Address Information
                        </AddressTitle>

                        <FormGroup>
                          <label htmlFor="street">Street Address</label>
                          <FormInput
                            type="text"
                            id="street"
                            name="dealerInfo.address.street"
                            value={formData.dealerInfo.address.street}
                            onChange={handleInputChange}
                            placeholder="Enter street address"
                            required
                          />
                        </FormGroup>

                        <FormRow>
                          <FormGroupRow>
                            <label htmlFor="city">City</label>
                            <FormInput
                              type="text"
                              id="city"
                              name="dealerInfo.address.city"
                              value={formData.dealerInfo.address.city}
                              onChange={handleInputChange}
                              placeholder="Enter city"
                              required
                            />
                          </FormGroupRow>

                          <FormGroupRow>
                            <label htmlFor="state">State</label>
                            <FormInput
                              type="text"
                              id="state"
                              name="dealerInfo.address.state"
                              value={formData.dealerInfo.address.state}
                              onChange={handleInputChange}
                              placeholder="Enter state"
                              required
                            />
                          </FormGroupRow>
                        </FormRow>

                        <FormRow>
                          <FormGroupRow>
                            <label htmlFor="zip">ZIP Code</label>
                            <FormInput
                              type="text"
                              id="zip"
                              name="dealerInfo.address.zip"
                              value={formData.dealerInfo.address.zip}
                              onChange={handleInputChange}
                              placeholder="Enter ZIP code"
                              required
                            />
                          </FormGroupRow>

                          <FormGroupRow>
                            <label htmlFor="country">Country</label>
                            <FormInput
                              type="text"
                              id="country"
                              name="dealerInfo.address.country"
                              value={formData.dealerInfo.address.country}
                              onChange={handleInputChange}
                              placeholder="Enter country"
                            required
                          />
                        </FormGroupRow>
                      </FormRow>

                        <LocationButton 
                          type="button" 
                          onClick={getCurrentLocation}
                          disabled={isGettingLocation}
                        >
                          <Navigation size={16} />
                          {isGettingLocation ? 'Getting Location...' : 'Get Current Location'}
                        </LocationButton>

                        <MapContainer onClick={handleMapClick}>
                          <MapPlaceholder>
                            <MapIcon>
                              <MapPin size={24} />
                            </MapIcon>
                            <MapText>
                              {formData.dealerInfo.address.latitude && formData.dealerInfo.address.longitude 
                                ? `Location set: ${formData.dealerInfo.address.latitude.toFixed(6)}, ${formData.dealerInfo.address.longitude.toFixed(6)}`
                                : 'Click to set location on map'
                              }
                            </MapText>
                            <MapButton type="button">
                              Open Map
                            </MapButton>
                          </MapPlaceholder>
                        </MapContainer>

                        <CoordinatesRow>
                          <FormGroupRow>
                            <label htmlFor="latitude">Latitude</label>
                            <CoordinateInput
                              type="number"
                              id="latitude"
                              name="dealerInfo.address.latitude"
                              value={formData.dealerInfo.address.latitude || ''}
                              onChange={handleInputChange}
                              placeholder="Enter latitude"
                              step="any"
                              required
                            />
                          </FormGroupRow>

                          <FormGroupRow>
                            <label htmlFor="longitude">Longitude</label>
                            <CoordinateInput
                              type="number"
                              id="longitude"
                              name="dealerInfo.address.longitude"
                              value={formData.dealerInfo.address.longitude || ''}
                              onChange={handleInputChange}
                              placeholder="Enter longitude"
                              step="any"
                              required
                            />
                          </FormGroupRow>
                        </CoordinatesRow>
                      </AddressSection>

                      <FormGroup>
                        <label htmlFor="password">Password</label>
                        <FormInput
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="confirmPassword">Re-type Password</label>
                        <FormInput
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirm your password"
                          required
                        />
                      </FormGroup>

                      <SignupButton type="submit" disabled={isLoading}>
                        {isLoading ? 'Creating account...' : 'Sign up'}
                      </SignupButton>
                    </SignupForm>

                    <Divider>
                      <span>Or</span>
                    </Divider>

                    <SocialLogin>
                      <SocialButton className="google">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                      </SocialButton>

                      <SocialButton className="apple">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.38-1.08-.52-2.07-.53-3.2 0-1.44.71-2.23.51-3.08-.38C2.79 15.25 3.51 7.59 9.28 7.31c1.33.07 2.25.8 3.04.86.79.05 1.8-.69 3.32-.84 1.8-.19 3.63 1.06 4.59 2.77-4.19 2.51-3.51 9.69.82 10.18zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="white" />
                        </svg>
                      </SocialButton>

                      <SocialButton className="facebook">
                       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-facebook" viewBox="0 0 16 16">
                       <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951"/></svg>
                      </SocialButton>
                    </SocialLogin>

                    <LoginLink>
                      <span>Already have an account?</span>
                      <Link to="/login" className="login-link">Log in</Link>
                    </LoginLink>
                  </SignupModal>
                </SignupContainer>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Map Selector Modal */}
      <MapSelector
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        onLocationSelect={handleMapLocationSelect}
        initialLat={formData.dealerInfo.address.latitude || 40.7128}
        initialLng={formData.dealerInfo.address.longitude || -74.0060}
      />

      {/* Footer */}
      <footer className="footer-area">
        <div className="containers">
          <div className="row">
            <div className="col-md-6">
              <div className="copy">
                <p>Copyright © 2024 Automotiveai.com</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="trams">
                <p><a href="#">Privacy Policy</a> | <a href="#">Terms & Conditions</a></p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12">
          <a href="#" className="scrolltotop"><i className="bi bi-arrow-up-circle"></i></a>
        </div>
      </footer>
    </>
  );
};

export default Signup; 